// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import { LinkProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TypographyProps } from "@store/MobiusOverrides/Typography/Typography";
import { linkStyles } from "@store/Themes/common/mixins/link";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React from "react";
import { css } from "styled-components";

export interface MappedLink {
    title: string;
    url: string;
    type?: string;
    excludeFromNavigation?: boolean;
    openInNewWindow?: boolean;
    numberOfColumns?: number | undefined;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu";
    children?: MappedLink[];
}

interface OwnProps {
    category?: MappedLink;
    maxSecondLevelChildren?: number;
    maxThirdLevelChildren?: number;
    numberOfColumns: number | undefined;
    onChildClick: () => void;
}

type Props = OwnProps;
export interface MenuCategoryPanelStyles {
    megaMenuGridItem?: GridItemProps;
    secondLevelHeaderWrapper?: InjectableCss;
    secondLevelHeaderText?: TypographyProps;
    seeMoreLink?: LinkProps;
    secondLevelLinkListWrapper?: GridItemProps;
    secondLevelLinkWrapper?: InjectableCss;
    secondLevelLink?: LinkProps;
    thirdLevelLinkList?: InjectableCss;
    thirdLevelLinkWrapper?: InjectableCss;
    thirdLevelLink?: LinkProps;
}

export const menuCategoryPanelStyles: MenuCategoryPanelStyles = {
    secondLevelHeaderWrapper: {
        css: css`
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            width: 100%;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    secondLevelHeaderText: {
        variant: "h3",
        color: "primary",
    },
    seeMoreLink: {
        css: css`
            ${linkStyles("secondary", "secondary.dark", false, true)};
            margin-top: ${pxToRem(16)};
            font-size: ${x => x.theme.design.typography.size.small};
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    secondLevelLinkWrapper: {
        css: css`
            ${spacingStyles(null, pxToRem(8))}
        `,
    },
    secondLevelLink: {
        css: css`
            ${linkStyles("text.linkAlt", "text.linkAltHover", false, true)};
            flex-direction: column;
            margin-top: ${pxToRem(16)};
            margin-bottom: ${pxToRem(8)};
        `,
    },
    secondLevelLinkListWrapper: {
        css: css`
            flex-direction: column;
        `,
    },
    thirdLevelLinkList: {
        css: css`
            ${spacingStyles(null, pxToRem(8))}
        `,
    },
    thirdLevelLinkWrapper: {
        css: css`
            ${spacingStyles(null, pxToRem(4))}
        `,
    },
    thirdLevelLink: {
        css: css`
            ${linkStyles("text.linkAlt", "text.linkAltHover", false, true)};
            flex-direction: column;
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
};

const styles = menuCategoryPanelStyles;

const MenuCategoryPanel: React.FC<Props> = ({
    category,
    maxSecondLevelChildren,
    maxThirdLevelChildren,
    numberOfColumns,
    onChildClick,
}) => {
    if (!category) {
        return null;
    }

    const secondLevelColumnGridWidth = numberOfColumns ? 12 / numberOfColumns : 4;

    return (
        <>
            <StyledWrapper {...styles.secondLevelHeaderWrapper}>
                <Typography {...styles.secondLevelHeaderText}>{category.title}</Typography>
                <Link href={category.url} onClick={onChildClick} {...styles.seeMoreLink}>
                    {translate("See all category products")}
                    {category?.children && <span>&nbsp;({category?.children.length})</span>}
                </Link>
            </StyledWrapper>
            {category?.children && (
                <>
                    <GridContainer {...styles.megaMenuGridItem} as="ul">
                        {category?.children
                            .filter(child => {
                                if (child.excludeFromNavigation) {
                                    return false;
                                }
                                return true;
                            })
                            .slice(0, maxSecondLevelChildren)
                            .map((child, index) => (
                                <GridItem
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    as="li"
                                    width={secondLevelColumnGridWidth as GridWidths}
                                    {...styles.secondLevelLinkListWrapper}
                                >
                                    <Link href={child.url} onClick={onChildClick} {...styles.secondLevelLink}>
                                        <Typography variant="h5">{child.title}</Typography>
                                    </Link>

                                    {child?.children && child?.children.length > 0 && (
                                        <StyledWrapper as="ul" {...styles.thirdLevelLinkList}>
                                            {child?.children
                                                .slice(0, maxThirdLevelChildren)
                                                .map((grandChild, index) => (
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    <StyledWrapper
                                                        as="li"
                                                        key={index}
                                                        {...styles.thirdLevelLinkWrapper}
                                                    >
                                                        <Link
                                                            href={grandChild.url}
                                                            onClick={onChildClick}
                                                            {...styles.thirdLevelLink}
                                                        >
                                                            {grandChild.title}
                                                        </Link>
                                                    </StyledWrapper>
                                                ))}
                                            {maxThirdLevelChildren && child?.children.length >= maxThirdLevelChildren && (
                                                <li>
                                                    <Link
                                                        href={child.url}
                                                        onClick={onChildClick}
                                                        {...styles.seeMoreLink}
                                                    >
                                                        {translate("See All")} ({child.children.length.toString()})
                                                    </Link>
                                                </li>
                                            )}
                                        </StyledWrapper>
                                    )}
                                </GridItem>
                            ))}
                    </GridContainer>
                </>
            )}
        </>
    );
};

export default MenuCategoryPanel;
